"use client";

// No-signup demo gallery for the marketing homepage. Links each demo into the
// public read-only canvas at /s/<id> (canAccessJob lets anyone open a demo
// audit). Renders nothing until there's at least one card, so the homepage has
// no empty section / CLS gap before any demos exist.
//
// Data source, in order:
//   1. The live /demos list (curated in the ops-console once is_demo ships).
//   2. FALLBACK_DEMO_IDS — a hardcoded list, hydrated directly from each job's
//      public state. This is what lets you preview the gallery on localhost
//      before deploying the backend, and acts as a sensible default in prod
//      until you've featured real demos. Each id must be a finished, publicly
//      viewable audit (i.e. owned by a Pro/Agency account, or flagged is_demo).
//      Replace/remove these once you curate demos for real.

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { AuditCta } from "../components/audit-cta";
import { GuestOnly } from "../components/guest-only";
import { fetchDemos, fetchJob, type JobSummary } from "../lib/api";
import type { JobState } from "../lib/audit-types";
import { track } from "../lib/track";
import { DEMO_JOB_IDS } from "../constants";

// Moved to constants.ts (DEMO_JOB_IDS) so the /s share page can label
// these audits "Live demo audit" too. Local alias keeps this file
// reading the same.
const FALLBACK_DEMO_IDS: string[] = DEMO_JOB_IDS;

interface DemoCard {
  id: string;
  label: string;
  source: string;
  thumbnailUrl?: string;
  findings?: number;
  viewportLabel: string;
}


function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function viewportLabelOf(viewports: string[] | undefined): string {
  if (!viewports || viewports.length === 0) return "Desktop";
  if (viewports.length > 1) return "Desktop + Mobile";
  return viewports[0] === "mobile" ? "Mobile" : "Desktop";
}

function cardFromSummary(j: JobSummary): DemoCard {
  const isImage = j.runMode === "imageAudit";
  return {
    id: j.id,
    label:
      j.displayName?.trim() ||
      j.siteName?.trim() ||
      (isImage ? "Uploaded screenshot" : hostnameOf(j.sourceUrl)),
    source: isImage ? "Uploaded image" : hostnameOf(j.sourceUrl),
    thumbnailUrl: j.thumbnailUrl,
    findings: j.findingsCount,
    viewportLabel: viewportLabelOf(j.viewports),
  };
}

function cardFromJob(job: JobState): DemoCard | null {
  if (job.status !== "done" || !job.result) return null;
  const r = job.result;
  const pages = [r.homepage, ...(r.pages ?? [])].filter(Boolean);
  let findings = 0;
  for (const p of pages) {
    findings +=
      (p.heuristicFindings?.length ?? 0) +
      (p.accessibilityFindings?.length ?? 0) +
      (p.bestPracticeFindings?.length ?? 0);
  }
  const shot = r.homepage?.shots?.[0];
  const isImage = job.runMode === "imageAudit";
  return {
    id: job.id,
    label: r.siteName?.trim() || hostnameOf(job.sourceUrl),
    source: isImage ? "Uploaded image" : hostnameOf(job.sourceUrl),
    thumbnailUrl: shot?.fullScreenshotPath || shot?.screenshotPath,
    findings,
    viewportLabel: viewportLabelOf(job.viewports),
  };
}

export function DemoGallery() {
  const [cards, setCards] = useState<DemoCard[] | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const live = await fetchDemos();
      if (live.length > 0) {
        if (!cancelled) setCards(live.map(cardFromSummary));
        return;
      }
      // No curated demos yet — hydrate the hardcoded fallback from public jobs.
      const jobs = await Promise.all(
        FALLBACK_DEMO_IDS.map((id) => fetchJob(id).catch(() => null))
      );
      if (cancelled) return;
      const fallback = jobs
        .map((j) => (j ? cardFromJob(j) : null))
        .filter((c): c is DemoCard => c !== null);
      setCards(fallback);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Recompute whether the prev / next nav arrows should be active.
   * Called on mount, on scroll, and on resize.
   */
  function refreshNavState() {
    const el = trackRef.current;
    if (!el) return;
    // 2px tolerance for sub-pixel rounding
    setCanPrev(el.scrollLeft > 2);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }

  useEffect(() => {
    if (!cards) return;
    refreshNavState();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", refreshNavState, { passive: true });
    window.addEventListener("resize", refreshNavState);
    return () => {
      el.removeEventListener("scroll", refreshNavState);
      window.removeEventListener("resize", refreshNavState);
    };
  }, [cards]);

  /**
   * Scroll by approximately one card width in the requested direction.
   * Uses the first card's width + the track gap as the step so we land
   * on a snap point cleanly regardless of viewport size.
   */
  function scrollByCard(direction: 1 | -1) {
    const el = trackRef.current;
    if (!el) return;
    const firstCard = el.querySelector<HTMLElement>(".demo-card");
    if (!firstCard) return;
    const style = window.getComputedStyle(el);
    const gap = parseFloat(style.columnGap || style.gap || "0") || 0;
    const step = firstCard.offsetWidth + gap;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  }

  if (!cards || cards.length === 0) return null;

  return (
    <section id="demos" className="demo-gallery" aria-labelledby="demo-gallery-heading">
      <div className="demo-gallery-head">
        {/* "Live" pulse + kicker. Same idiom as .lp-ai-trust-dot but
            tinted teal to signal "interactive product" instead of the
            AI trust line's soft green. The pulse animation is purely
            decorative — `aria-hidden` keeps it out of the AT tree, and
            the kicker carries the same "Live demos" meaning in text. */}
        <p className="lp-kicker demo-gallery-kicker">
          <span className="demo-gallery-live-dot" aria-hidden="true" />
          Live demos · Click any card to open
        </p>
        <h2 id="demo-gallery-heading">
          Real audits. Real findings. Click any one to open it.
        </h2>
        <p className="demo-gallery-sub">
          Same heuristics, same accessibility checks, same canvas you&rsquo;d
          see on your own site. These are public audits — nothing
          staged.
        </p>
      </div>

      <div className="demo-gallery-carousel">
        {/* Prev / next nav arrows. The track itself is scrollable by
            touch/trackpad too — these buttons are a discoverability
            cue and a click target for visitors on a mouse. */}
        <button
          type="button"
          className="demo-gallery-nav demo-gallery-nav-prev"
          aria-label="Show previous demos"
          onClick={() => scrollByCard(-1)}
          disabled={!canPrev}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path
              d="M11 4 6 9l5 5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div
          className="demo-gallery-track"
          ref={trackRef}
          role="region"
          aria-label="Live demo audits"
        >
          {cards.map((c) => (
            <Link
              key={c.id}
              href={`/s/${c.id}`}
              className="demo-card"
              aria-label={`View the ${c.label} demo audit`}
              onClick={() => track("Demo Opened", { site: c.label })}
            >
              {/* Browser chrome — light recognition that this is a
                  captured page. No annotation overlays on the shot
                  itself; the body below carries the audit story. */}
              <div className="demo-card-bar">
                <span className="demo-card-dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                <span className="demo-card-address">{c.source}</span>
              </div>
              <div className="demo-card-shot">
                {c.thumbnailUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={c.thumbnailUrl} alt="" loading="lazy" />
                ) : (
                  <div className="demo-card-shot-empty" />
                )}
              </div>
              {/* Body — identity, finding count, and a full-width
                  design-system secondary CTA. */}
              <div className="demo-card-body">
                <div className="demo-card-titles">
                  <span className="demo-card-name">{c.label}</span>
                  <span className="demo-card-meta">
                    {c.viewportLabel} audit
                    {typeof c.findings === "number" && c.findings > 0 ? (
                      <>
                        {" · "}
                        <span className="demo-card-meta-strong">
                          {c.findings} {c.findings === 1 ? "finding" : "findings"}
                        </span>
                      </>
                    ) : null}
                  </span>
                </div>
                <span className="demo-card-open">
                  See what we found
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path
                      d="M3 7h8M7.5 3.5 11 7l-3.5 3.5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </Link>
          ))}
        </div>

        <button
          type="button"
          className="demo-gallery-nav demo-gallery-nav-next"
          aria-label="Show next demos"
          onClick={() => scrollByCard(1)}
          disabled={!canNext}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path
              d="M7 4 12 9l-5 5"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="demo-gallery-cta">
        {/* Contextual CTA copy. The default "Start a free audit" works
            everywhere on the page, but here — right after the visitor
            watched a real audit of a name-brand site — the sharper
            phrasing is "now do it on your own." Same destination, same
            primary-button styling; only the guest label changes (auth
            users still see "Start a new audit"). */}
        <AuditCta guestLabel="Check your site free" surface="demo-gallery" />
        <GuestOnly>
          <span className="demo-gallery-cta-note">
            No signup to start · No card needed
          </span>
        </GuestOnly>
      </div>
    </section>
  );
}
